
import React, { useState, useEffect, useRef } from 'react';
import {
  Container, Grid, Card, CardContent, Typography,
  Dialog, DialogContent, DialogActions, Button, Box, useTheme, CircularProgress, Alert
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Document, Page, pdfjs } from 'react-pdf';
import { motion } from 'framer-motion';

// Worker served via CDN - avoids webpack worker bundling complexity with CRA
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Renders page 1 of a PDF as a canvas, sized to its container
const PdfPreview = ({ file }) => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      setContainerWidth(Math.floor(entries[0].contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        backgroundColor: '#ffffff',
        // Scale the canvas down if the PDF page is taller than the card
        '& .react-pdf__Page': {
          maxHeight: '100%',
        },
        '& canvas': {
          maxWidth: '100%',
          height: 'auto !important',
        },
      }}
    >
      {containerWidth > 0 && (
        <Document
          file={file}
          loading={
            <Box sx={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          }
          error={
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
              <PictureAsPdfIcon sx={{ fontSize: 48, color: '#999', opacity: 0.5 }} />
              <Typography variant="caption" sx={{ color: '#999' }}>Preview unavailable</Typography>
            </Box>
          }
        >
          <Page
            pageNumber={1}
            width={containerWidth}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>
      )}
    </Box>
  );
};

const ProgramShowcase = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [currentFile, setCurrentFile] = useState('');
  const [showcaseData, setShowcaseData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch('/data/program_showcase.json')
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to load showcase data');
        }
        return response.json();
      })
      .then(data => {
        setShowcaseData(data.showcase.filter(item => item.active));
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading schedule:', err);
        setError('Failed to load schedule. Please try again later.');
        setLoading(false);
      });
  }, []);

  const isPdf = (url) => url.toLowerCase().endsWith('.pdf');
  const isHtml = (url) => url.toLowerCase().endsWith('.html');
  const pdfModalSrc = (url) => `${url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`;

  const handleFileClick = (file) => {
    setCurrentFile(file);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDownload = async (fileUrl) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileUrl.split('/').pop() || 'schedule.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed, opening in new tab as fallback:', error);
      window.open(fileUrl, '_blank');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, padding: 0 }}>
      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Program Showcase Grid */}
      {!loading && !error && (
        <Grid container spacing={4} direction="column">
          {showcaseData.map((program, index) => (
            <Grid item xs={12} key={index}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
              <Card
                sx={{
                  display: 'flex',
                  flexDirection: {
                    xs: 'column',
                    md: index % 2 === 0 ? 'row' : 'row-reverse'
                  },
                  borderRadius: 3,
                  boxShadow: 3,
                  overflow: 'hidden',
                  position: 'relative',
                  backgroundColor: theme.palette.background.paper,
                  height: { xs: 'auto', md: 420 },
                  borderTop: `2px solid ${theme.palette.primary.main}`,
                }}
              >
                {/* Clickable Preview */}
                <Box
                  sx={{
                    width: { xs: '100%', md: '70%' },
                    height: { xs: 300, md: '100%' },
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    backgroundColor: theme.palette.background.default,
                    '&:hover .view-chip': {
                      opacity: 1,
                      transform: 'translateY(0)',
                    },
                  }}
                  onClick={() => handleFileClick(program.image)}
                >
                  {isPdf(program.image) ? (
                    <PdfPreview file={program.image} />
                  ) : isHtml(program.image) ? (
                    <Box sx={{ width: '100%', height: '100%', overflow: 'hidden', position: 'relative', backgroundColor: '#f4f7fb' }}>
                      <Box
                        component="iframe"
                        src={program.image}
                        title={program.title}
                        scrolling="no"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '200%',
                          height: '200%',
                          border: 'none',
                          transform: 'scale(0.5)',
                          transformOrigin: 'top left',
                          pointerEvents: 'none',
                        }}
                      />
                    </Box>
                  ) : (
                    /* PNG/image: full document visible */
                    <Box
                      component="img"
                      src={program.image}
                      alt={program.title}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'block',
                      }}
                    />
                  )}

                  {/* View chip - appears on hover, bottom-right, non-obscuring */}
                  <Box
                    className="view-chip"
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      right: 16,
                      backgroundColor: theme.palette.primary.main,
                      color: '#fff',
                      px: 2,
                      py: 0.75,
                      borderRadius: 20,
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      fontFamily: theme.typography.fontFamily,
                      opacity: 0,
                      transform: 'translateY(6px)',
                      transition: 'opacity 0.25s ease, transform 0.25s ease',
                      pointerEvents: 'none',
                      userSelect: 'none',
                    }}
                  >
                    View Schedule
                  </Box>
                </Box>

                {/* Text Content */}
                <CardContent
                  sx={{
                    width: { xs: '100%', md: '30%' },
                    height: { xs: 'auto', md: '100%' },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    textAlign: 'left',
                    padding: 3,
                    gap: 0,
                  }}
                >
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ color: theme.palette.text.primary, fontWeight: 'bold', mb: 2.5 }}
                  >
                    {program.title}
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          opacity: 0.6,
                          display: 'block',
                          mb: 0.25,
                        }}
                      >
                        Start Date
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: theme.palette.text.primary, fontWeight: 600, lineHeight: 1.2 }}
                      >
                        {program.startDate}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          opacity: 0.6,
                          display: 'block',
                          mb: 0.25,
                        }}
                      >
                        End Date
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: theme.palette.text.primary, fontWeight: 600, lineHeight: 1.2 }}
                      >
                        {program.endDate}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Full View Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            width: {
              xs: '95%',
              sm: '90%',
              md: '85%',
            },
            height: '90vh',
            maxWidth: 'none',
            m: { xs: 1, sm: 2, md: 3 },
            borderRadius: 2,
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
        }}
      >
        <DialogContent sx={{ p: 0, flex: 1, overflow: 'hidden' }}>
          {isPdf(currentFile) ? (
            <Box
              component="iframe"
              src={pdfModalSrc(currentFile)}
              title="Full View"
              sx={{
                width: '100%',
                height: '100%',
                border: 'none',
                display: 'block',
              }}
            />
          ) : isHtml(currentFile) ? (
            <Box
              component="iframe"
              src={currentFile}
              title="Full View"
              sx={{
                width: '100%',
                height: '100%',
                border: 'none',
                display: 'block',
              }}
            />
          ) : (
            <Box
              component="img"
              src={currentFile}
              alt="Full View"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{
          p: { xs: 2, sm: 3 },
          justifyContent: 'space-between',
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.7)',
          borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
        }}>
          <Box>
            <Button
              variant="outlined"
              component="a"
              href={currentFile}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<OpenInNewIcon />}
              sx={{
                mr: 1,
                textTransform: 'none',
                borderRadius: 2,
                fontFamily: theme.typography.fontFamily,
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'inherit',
                color: theme.palette.mode === 'dark' ? '#fff' : 'inherit'
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Open Full</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Open</Box>
            </Button>
            {!isHtml(currentFile) && (
              <Button
                variant="contained"
                onClick={() => handleDownload(currentFile)}
                startIcon={<DownloadIcon />}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  fontFamily: theme.typography.fontFamily,
                  bgcolor: theme.palette.secondary.main,
                  color: '#fff',
                  '&:hover': {
                    bgcolor: theme.palette.secondary.dark,
                  }
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Download Schedule</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Download</Box>
              </Button>
            )}
          </Box>
          <Button onClick={handleClose} sx={{ color: theme.palette.mode === 'dark' ? '#fff' : 'inherit', fontWeight: 'bold', fontFamily: theme.typography.fontFamily }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProgramShowcase;
